import { useEffect, useRef, type ReactNode } from "react";
import styled, { keyframes } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { Box } from "orcs-design-system";

import { inboxScrollBehavior } from "./inboxTransition";

const arrivePulse = keyframes`
  from {
    box-shadow:
      inset 3px 0 0 var(--inbox-rail),
      0 0 0 6px var(--inbox-ring);
  }
  to {
    box-shadow: inset 3px 0 0 var(--inbox-rail);
  }
`;

/** Selection is a primary left rail plus a primary ring. Hover is the surface tint. */
const CardButton = styled(Box)`
  appearance: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  box-shadow: inset 3px 0 0 transparent;
  transition:
    box-shadow 120ms ease,
    border-color 120ms ease,
    background-color 120ms ease;

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }

  &:focus-visible {
    outline: none;
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 transparent,
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }

  &[aria-current="true"] {
    --inbox-rail: ${themeGet("colors.primary")};
    --inbox-ring: ${themeGet("colors.primary30")};
    border-color: ${themeGet("colors.primary")};
    box-shadow: inset 3px 0 0 ${themeGet("colors.primary")};
  }

  &[aria-current="true"][data-arrived="true"] {
    animation: ${arrivePulse} 600ms ease-out 1;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &[aria-current="true"]:focus-visible {
    border-color: ${themeGet("colors.focusOutline")};
    box-shadow:
      inset 3px 0 0 ${themeGet("colors.primary")},
      ${themeGet("shadows.thickOutline")} ${themeGet("colors.primary30")};
  }
`;

const InboxCardShell = ({
  itemKey,
  selected,
  arrived = false,
  onSelect,
  children,
}: {
  itemKey: string;
  selected: boolean;
  /** One-shot: the auto-advance just opened this card, so pulse its ring. */
  arrived?: boolean;
  onSelect: (key: string) => void;
  children: ReactNode;
}) => {
  const cardRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!selected) {
      return;
    }
    cardRef.current?.scrollIntoView?.({
      block: "nearest",
      behavior: inboxScrollBehavior(),
    });
  }, [selected]);
  return (
    <CardButton
      ref={cardRef}
      forwardedAs="button"
      type="button"
      width="100%"
      p="s"
      bg="surface"
      boxBorder="default"
      borderRadius={2}
      aria-current={selected ? "true" : undefined}
      data-arrived={selected && arrived ? "true" : undefined}
      data-testid={`inbox-card-${itemKey}`}
      onClick={() => onSelect(itemKey)}
    >
      {children}
    </CardButton>
  );
};

export default InboxCardShell;
